import Image, { StaticImageData } from 'next/image';

import { AppStoreButtons } from '../../Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { Heading, HeadingVariant } from '../../Heading/Heading';
import { QRCode } from '../../QRCode/QRCode';
import { Text } from '../../Text/Text';

import phone from '../assets/PhonePreviewDesktop.png';
import phoneMobile from '../assets/PhonePreviewMobile.png';
import arrowBlack from '../assets/arrow-black.svg';
import { twMerge } from 'tailwind-merge';
import { APPDOWNLOADMODAL_HEADER, APPDOWNLOADMODAL_TEXT } from '../AppDownloadModal';
import { Source } from '../../NextImage/Source';

interface IProps {
  appDownloadContext?: string;
  appDownloadSource: string;
  appDownloadSourceId?: string;
  className?: string;
  contentHeader?: string;
  contentHeaderClassName?: string;
  contentHeaderVariant?: HeadingVariant;
  contentText?: string;
  contentWrapperClassName?: string;
  image?: StaticImageData;
  imageMobile?: StaticImageData;
  imageWrapperClassName?: string;
  showQRCodeHint?: boolean;
  showTextOnMobile?: boolean;
  testID?: string;
}

/**
 * Component for displaying the content of the App Download Modal.
 * This component is mainly used in the AppDownloadModal component but can also be used in other components without the modal.
 * It displays either qr code or app store buttons or both based on the device type and screen size.
 * Appstore buttons are always displayed on mobile and tablet screen sizes. If we can detect the device type we show only the relevant store button.
 * The qr code is displayed on tablet and desktop screen sizes regardless of the device type.
 * Per default the contents text is not shown on mobile devices but this can be overwritten by the showTextOnMobile prop.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.appDownloadSource - The source URL for the app download.
 * @param {string} [props.className] - Additional class names for the root component.
 * @param {string} [props.contentHeader=APPDOWNLOADMODAL_HEADER] - The header text for the content section.
 * @param {HeadingVariant} [props.contentHeaderVariant='lg'] - The variant of the header text.
 * @param {string} [props.contentText=APPDOWNLOADMODAL_TEXT] - The text content for the content section.
 * @param {string} [props.contentWrapperClassName] - Additional class names for the content wrapper.
 * @param {StaticImageData} [props.image=phone] - The image to be displayed on larger screens.
 * @param {StaticImageData} [props.imageMobile=phoneMobile] - The image to be displayed on mobile screens.
 * @param {string} [props.imageWrapperClassName] - Additional class names for the image wrapper.
 * @param {boolean} [props.showQRCodeHint=false] - Flag to determine if the QR code hint should be displayed.
 * @param {boolean} [props.showTextOnMobile=false] - Flag to determine if the text should be displayed on mobile devices.
 * @param {string} [props.testID='AppDownloadContent'] - The test ID for the component, used for testing purposes.
 *
 * @returns {JSX.Element} The rendered component.
 */
export const AppDownloadContent = ({
  appDownloadContext,
  appDownloadSource,
  appDownloadSourceId,
  className,
  contentHeader = APPDOWNLOADMODAL_HEADER,
  contentHeaderClassName,
  contentHeaderVariant = 'lg',
  contentText = APPDOWNLOADMODAL_TEXT,
  contentWrapperClassName,
  image = phone,
  imageMobile = phoneMobile,
  imageWrapperClassName,
  showQRCodeHint = false,
  showTextOnMobile = false,
  testID = 'AppDownloadContent',
}: IProps) => {
  return (
    <div
      data-testid={testID}
      className={twMerge(`flex h-full flex-col justify-end sm:flex-row`, className)}
    >
      <div
        className={twMerge(
          `flex w-full items-end justify-center px-16 pt-8 sm:w-2/5 sm:p-12`,
          imageWrapperClassName,
        )}
      >
        <picture>
          <Source media="(min-width: 600px)" src={image} />
          <Image alt="App Section" src={imageMobile} className="flex object-contain" />
        </picture>
      </div>
      <div
        className={twMerge(
          `mt-4 flex w-full flex-col items-start justify-between gap-4 px-7 sm:mt-0 sm:w-3/5 sm:px-0 sm:py-14 md:pr-20`,
          contentWrapperClassName,
        )}
      >
        <div className="flex flex-col gap-4 sm:p-4">
          <Heading
            as="p"
            variant={contentHeaderVariant}
            className={twMerge('m-0 text-center sm:text-left', contentHeaderClassName)}
          >
            {contentHeader}
          </Heading>
          {contentText.length > 0 && (
            <Text
              variant="md"
              className={`${showTextOnMobile ? 'block' : 'hidden sm:block'} m-0 text-center sm:text-left`}
            >
              {contentText}
            </Text>
          )}
        </div>
        <div className="flex w-full flex-col items-center justify-center gap-4 sm:flex-row sm:justify-end sm:p-4">
          <div className="flex items-center justify-center pb-6 sm:pb-0 md:hidden">
            <AppStoreButtons
              orientation="vertical"
              context={appDownloadContext}
              source={appDownloadSource}
              sourceId={appDownloadSourceId}
            />
          </div>
          {showQRCodeHint && (
            <div className="relative hidden pb-2 md:flex">
              <Text className={`text-trench m-0 -rotate-12 whitespace-pre-line`} variant="sm">
                {`Scan the QR code 
                          to download the app!`}
              </Text>
              <Image
                alt="Arrow pointing to QR code"
                src={arrowBlack}
                className="absolute right-0 bottom-0 translate-y-full"
                unoptimized
              />
            </div>
          )}
          <QRCode
            size={140}
            imageSize={35}
            className="hidden sm:flex sm:justify-end sm:p-4"
            source={appDownloadSource}
            showOnMobileDevice
          />
        </div>
      </div>
    </div>
  );
};
